/**
 * Token Refresh Manager
 * Handles token refresh with:
 * - Locking to prevent concurrent refreshes
 * - Refresh token expiration checking
 * - Rate limiting protection
 * - Token versioning to prevent stale tokens
 * - Retry logic with exponential backoff
 */

class TokenRefreshManager {
  constructor() {
    // Lock for concurrent refresh prevention
    this.refreshInProgress = null;
    this.refreshQueue = [];
    
    // Token versioning (increments on each refresh)
    this.tokenVersion = 0;
    this.tokenVersionKey = 'faithinsight_token_version';
    
    // Rate limiting
    this.refreshAttempts = [];
    this.maxRefreshAttempts = 5;
    this.refreshWindowMs = 60000; // 1 minute
    
    // Initialize token version from localStorage
    this.loadTokenVersion();
  }
  
  /**
   * Load token version from localStorage
   */
  loadTokenVersion() {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(this.tokenVersionKey);
      if (stored) {
        this.tokenVersion = parseInt(stored, 10) || 0;
      }
    }
  }
  
  /**
   * Save token version to localStorage
   */
  saveTokenVersion() {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.tokenVersionKey, this.tokenVersion.toString());
    }
  }
  
  /**
   * Get current token version
   */
  getTokenVersion() {
    return this.tokenVersion;
  }
  
  /**
   * Increment token version (call after successful refresh)
   */
  incrementTokenVersion() {
    this.tokenVersion++;
    this.saveTokenVersion();
    return this.tokenVersion;
  }
  
  /**
   * Check if refresh token is expired
   */
  isRefreshTokenExpired() {
    if (typeof window === 'undefined') return true;
    
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    if (!refreshToken) return true;
    
    try {
      // Decode JWT without verification (just to check expiration)
      const parts = refreshToken.split('.');
      if (parts.length !== 3) return true;
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      
      if (!exp) return true;
      
      // Check if expired (with 5 minute buffer)
      const expirationTime = exp * 1000;
      const bufferTime = 5 * 60 * 1000; // 5 minutes
      return Date.now() >= (expirationTime - bufferTime);
    } catch (error) {
      console.warn('[Token Refresh Manager] Error checking refresh token expiration:', error);
      return true; // Assume expired if we can't parse
    }
  }
  
  /**
   * Check if refresh token will expire soon (within 1 hour)
   */
  isRefreshTokenExpiringSoon() {
    if (typeof window === 'undefined') return false;
    
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    if (!refreshToken) return true;
    
    try {
      const parts = refreshToken.split('.');
      if (parts.length !== 3) return true;
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      
      if (!exp) return true;
      
      const expirationTime = exp * 1000;
      const oneHourFromNow = Date.now() + (60 * 60 * 1000);
      return expirationTime <= oneHourFromNow;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Check if refresh token is available
   */
  hasRefreshToken() {
    if (typeof window === 'undefined') return false;
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    return !!refreshToken && !this.isRefreshTokenExpired();
  }
  
  /**
   * Check rate limiting for refresh attempts
   */
  isRateLimited() {
    const now = Date.now();
    
    // Remove old attempts outside the window
    this.refreshAttempts = this.refreshAttempts.filter(
      timestamp => now - timestamp < this.refreshWindowMs
    );
    
    // Check if we've exceeded the limit
    return this.refreshAttempts.length >= this.maxRefreshAttempts;
  }
  
  /**
   * Record a refresh attempt
   */
  recordRefreshAttempt() {
    this.refreshAttempts.push(Date.now());
  }
  
  /**
   * Clear rate limit tracking
   */
  clearRateLimit() {
    this.refreshAttempts = [];
  }
  
  /**
   * Refresh token with locking (prevents concurrent refreshes)
   */
  async refreshTokenWithLock(baseURL) {
    // If refresh is already in progress, return the existing promise
    if (this.refreshInProgress) {
      console.log('[Token Refresh Manager] Refresh already in progress, waiting...');
      return this.refreshInProgress;
    }
    
    // Check rate limiting
    if (this.isRateLimited()) {
      const error = new Error('Too many token refresh attempts. Please wait a moment.');
      error.rateLimited = true;
      throw error;
    }
    
    // Check if refresh token is available
    if (!this.hasRefreshToken()) {
      const error = new Error('Refresh token not available or expired. Please log in again.');
      error.noRefreshToken = true;
      throw error;
    }
    
    // Check if refresh token is expired
    if (this.isRefreshTokenExpired()) {
      const error = new Error('Refresh token has expired. Please log in again.');
      error.refreshTokenExpired = true;
      throw error;
    }
    
    // Create refresh promise
    const refreshPromise = this._performRefresh(baseURL);
    
    // Store promise so other requests can wait for it
    this.refreshInProgress = refreshPromise;
    
    try {
      const result = await refreshPromise;
      
      // Increment token version on successful refresh
      this.incrementTokenVersion();
      
      return result;
    } catch (error) {
      // Don't record rate limit for network errors
      if (!error.networkError) {
        this.recordRefreshAttempt();
      }
      throw error;
    } finally {
      // Clear in-progress flag
      this.refreshInProgress = null;
      
      // Process all queued requests (not just one)
      while (this.refreshQueue.length > 0) {
        const next = this.refreshQueue.shift();
        next();
      }
    }
  }
  
  /**
   * Perform the actual token refresh
   */
  async _performRefresh(baseURL, retryCount = 0) {
    const maxRetries = 3;
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    try {
      console.log('[Token Refresh Manager] Attempting to refresh token...');
      
      const refreshResponse = await fetch(`${baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (!refreshResponse.ok) {
        const errorData = await refreshResponse.json().catch(() => ({}));
        
        if (refreshResponse.status === 401) {
          // Refresh token invalid or expired
          const error = new Error(errorData.message || 'Refresh token invalid or expired');
          error.refreshTokenExpired = true;
          throw error;
        }
        
        if (refreshResponse.status === 429) {
          // Rate limited
          const error = new Error('Too many refresh attempts. Please wait.');
          error.rateLimited = true;
          throw error;
        }
        
        // Other server errors - retry if possible
        if (retryCount < maxRetries && refreshResponse.status >= 500) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`[Token Refresh Manager] Server error, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this._performRefresh(baseURL, retryCount + 1);
        }
        
        throw new Error(errorData.message || 'Token refresh failed');
      }
      
      const refreshData = await refreshResponse.json();
      
      if (!refreshData.success || !refreshData.token) {
        throw new Error(refreshData.message || 'Token refresh failed');
      }
      
      // Update tokens in localStorage
      localStorage.setItem('faithinsight_token', refreshData.token);
      if (refreshData.user) {
        localStorage.setItem('faithinsight_user', JSON.stringify(refreshData.user));
      }
      
      // Update refresh token if rotated
      if (refreshData.refreshToken) {
        localStorage.setItem('faithinsight_refresh_token', refreshData.refreshToken);
      }
      
      console.log('[Token Refresh Manager] Token refreshed successfully');
      
      return {
        token: refreshData.token,
        user: refreshData.user,
        refreshToken: refreshData.refreshToken
      };
      
    } catch (error) {
      // Mark network errors
      if (error.name === 'TypeError' || error.message.includes('fetch')) {
        error.networkError = true;
      }
      
      // Retry on network errors
      if (error.networkError && retryCount < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        console.log(`[Token Refresh Manager] Network error, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this._performRefresh(baseURL, retryCount + 1);
      }
      
      throw error;
    }
  }
  
  /**
   * Wait for in-progress refresh to complete
   */
  async waitForRefresh() {
    if (!this.refreshInProgress) {
      return null;
    }
    
    return new Promise((resolve) => {
      this.refreshQueue.push(resolve);
    });
  }
  
  /**
   * Check if refresh is in progress
   */
  isRefreshInProgress() {
    return !!this.refreshInProgress;
  }
  
  /**
   * Get time until refresh token expires (in milliseconds)
   */
  getTimeUntilRefreshTokenExpires() {
    if (typeof window === 'undefined') return null;
    
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    if (!refreshToken) return null;
    
    try {
      const parts = refreshToken.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      
      if (!exp) return null;
      
      const expirationTime = exp * 1000;
      const now = Date.now();
      
      return Math.max(0, expirationTime - now);
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
const tokenRefreshManager = new TokenRefreshManager();
export default tokenRefreshManager;
